import { Form, Formik } from "formik";
import { useRef, useState } from "react";
import moment from "moment/moment";
import { useNavigate } from "react-router-dom";
// @ts-expect-error pure js
import { sanitizeValues } from "../../models/utils";
// @ts-expect-error pure js
import { edlRealisationValidationSchema } from "../../models/edl";
import EdlFirstPageForm from "./EdlFirstPageForm";
import EdlLastPageForm from "./EdlLastPageForm";
import EdlTypeStep from "./EdlTypeStep";
// @ts-expect-error pure js
import useAuthenticatedAxios from "../../hooks/use-authenticated-axios";
// @ts-expect-error pure js
import TabbedForm from "../UI/TabbedForm";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
import ChooseSendFileMethod from "../UI/ChooseSendFileMethod";
// @ts-expect-error pure js
import { ProfileStatus } from "../../models/profile";
import EdlShow from "./EdlShow";
// @ts-expect-error pure js
import useProfileBailleurs from "../../hooks/use-profile-bailleurs";
import ModalDialog, { ModalDialogBody } from "../UI/ModalDialog";
import { useToast } from "../UI/Toast/useToast";
import type { Tenant } from "../../models/tenant";
import type { Property } from "../../models/property";
import type { Profile, ProfileBailleur } from "../Profile/types";
import styles from "./EdlRealisation.module.css";

interface TabbedFormHandle {
  resetTab: () => void;
}

interface Signatory {
  nom: string;
  role: string;
}

interface EdlProfileState {
  present: string;
  nom: string;
  represente_prenom: string;
  represente_nom: string;
  represente_qualite: string;
}

interface EdlRealisationValues {
  profiles: EdlProfileState[];
  profiles_bailleurs?: EdlProfileState[];
  type_edl: string;
  indice_dpe: string;
  description: string;
  date: string;
  type_envoi: string;
  signatures: (Signatory & { img: string })[];
  paraphes: (Signatory & { img: string })[];
}

interface EdlRealisationProps {
  tenant: Tenant;
  property?: Property;
  updatePropertyCles: (
    propertyId: number,
    cles: unknown,
  ) => void | Promise<unknown>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmitted: () => void;
}

function profileFullName(profile: Profile): string {
  return profile.type === 1
    ? `${profile.prenom} ${profile.nom}`
    : `${profile.prenom} ${profile.nom} gérant de la société ${profile.nom_societe}`;
}

export default function EdlRealisation({
  tenant,
  property,
  updatePropertyCles,
  open,
  onOpenChange,
  onSubmitted,
}: EdlRealisationProps) {
  const axios = useAuthenticatedAxios();
  const [edlPreview, setEdlPreview] = useState<string | undefined>();
  const formRef = useRef<TabbedFormHandle>(null);
  const { profileBailleursById } = useProfileBailleurs({ withById: true }) as {
    profileBailleursById: Record<number, ProfileBailleur | undefined>;
  };
  const toast = useToast();
  const nav = useNavigate();

  if (!property) return null;
  const propertyId = property.id;
  const profileNomBailleur =
    profileBailleursById[property.id_profile_nom_owner];

  // Un locataire sous représentation légale n'assiste pas à l'EDL et ne le
  // signe pas : ses représentants le remplacent, dans la liste des présents
  // comme pour la signature.
  const tenantAttendees = (tenant?.profiles ?? [])
    .filter(
      (p) => p.status === ProfileStatus.Actif.status && p.garantor_of === null,
    )
    .flatMap((p) => {
      const representants = (p.legal_representatives ?? []).filter(
        (rep) => rep.status === ProfileStatus.Actif.status,
      );
      return representants.length > 0 ? representants : [p];
    });

  const signatories: Signatory[] = [
    ...(profileNomBailleur?.profiles ?? []).map((p) => ({
      nom: profileFullName(p),
      role: "bailleur",
    })),
    ...tenantAttendees.map((p) => ({
      nom: profileFullName(p),
      role: "locataire",
    })),
  ];

  async function submitHandler(values: EdlRealisationValues, preview = false) {
    const sanitizedValues: Record<string, unknown> = {
      ...sanitizeValues(values),
    };
    const typeEnvoi = sanitizedValues.type_envoi;
    delete sanitizedValues.type_envoi;

    const params: { type_envoi: unknown; preview?: boolean } = {
      type_envoi: typeEnvoi,
    };
    if (preview) {
      params.preview = true;
    }
    try {
      const response = await axios.post(
        `/biens/${propertyId}/generate-edl/${tenant.id}`,
        sanitizedValues,
        { params },
      );
      if (preview) {
        setEdlPreview(response.data.download_url);
      } else {
        onSubmitted();
        nav(
          `/bailleur/tenants/${tenant.id}?tab-tenant-show=documents-courriers`,
        );
      }
    } catch (error) {
      toast.error({ title: handleAPIError(error) });
    }
  }

  const initialValues: EdlRealisationValues = {
    profiles: tenantAttendees.map((p) => ({
      present: "Présent",
      nom: profileFullName(p),
      represente_prenom: "",
      represente_nom: "",
      represente_qualite: "",
    })),
    profiles_bailleurs: profileNomBailleur?.profiles?.map((p) => ({
      present: "Présent",
      nom: profileFullName(p),
      represente_prenom: "",
      represente_nom: "",
      represente_qualite: "",
    })),
    type_edl: tenant.preavis ? "Sortie" : "Entrée",
    indice_dpe: "",
    description: "",
    date: moment().format("YYYY-MM-DD"),
    type_envoi: "elec",
    signatures: signatories.map((s) => ({ ...s, img: "" })),
    paraphes: signatories.map((s) => ({ ...s, img: "" })),
  };

  return (
    <Formik<EdlRealisationValues>
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={(values) => submitHandler(values)}
      validationSchema={edlRealisationValidationSchema}
    >
      <TabbedForm id="do-edl-form" ref={formRef}>
        <ModalDialog
          open={open}
          size="xl"
          onOpenChange={(nextOpen: boolean) => {
            if (!nextOpen) formRef.current?.resetTab();
            onOpenChange(nextOpen);
          }}
        >
          <div className={styles.stepperBar}>
            <TabbedForm.TabList>
              <TabbedForm.Tab panelId="type">Type</TabbedForm.Tab>
              <TabbedForm.Tab panelId="identification">
                Identification
              </TabbedForm.Tab>
              <TabbedForm.Tab panelId="piece">Pièces</TabbedForm.Tab>
              <TabbedForm.Tab panelId="conclusion">Compteurs</TabbedForm.Tab>
              <TabbedForm.Tab panelId="document">
                Validation & envoi
              </TabbedForm.Tab>
            </TabbedForm.TabList>
          </div>
          <Form>
            <TabbedForm.TabPanel id="type">
              <EdlTypeStep preavis={tenant?.preavis} />
            </TabbedForm.TabPanel>
            <TabbedForm.TabPanel id="identification">
              <EdlFirstPageForm
                tenantProfiles={tenantAttendees}
                bailleurProfiles={profileNomBailleur?.profiles}
              />
            </TabbedForm.TabPanel>
            <TabbedForm.TabPanel id="piece">
              <ModalDialogBody>
                <EdlShow
                  propertyId={propertyId}
                  isRealisation
                  property={property}
                  updatePropertyCles={updatePropertyCles}
                />
              </ModalDialogBody>
              <TabbedForm.Actions sticky />
            </TabbedForm.TabPanel>
            <TabbedForm.TabPanel id="conclusion">
              <ModalDialogBody>
                <EdlLastPageForm propertyId={propertyId} />
              </ModalDialogBody>
              <TabbedForm.Actions sticky />
            </TabbedForm.TabPanel>
            <TabbedForm.TabPanel id="document">
              <ChooseSendFileMethod
                propertyId={tenant?.id_bien}
                profiles={tenant?.profiles}
                drawnSignatories={signatories}
                onPreview={(values: EdlRealisationValues) =>
                  submitHandler(values, true)
                }
                previewUrl={edlPreview}
                sticky
              />
            </TabbedForm.TabPanel>
          </Form>
        </ModalDialog>
      </TabbedForm>
    </Formik>
  );
}
